import React, { useMemo, useState } from 'react';
import { OrganizationSuggestionsResponse } from '../../../store/signup/types';
import GivelifyModal from '../../../theme/GivelifyModal';
import Label18 from '../../../theme/labels/Label18';
import Button from '@material-ui/core/Button/Button';
import Checkbox from '@material-ui/core/Checkbox/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel/FormControlLabel';
import createStyles from '@material-ui/core/styles/createStyles';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Theme } from '@material-ui/core/styles';
import Label16 from '../../../theme/labels/Label16';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from 'store/hooks';
import { signSetAction } from '../../../store/signup/actions';
import { useRouteConfig } from '../utils/useRouteConfig';
import { PATH } from '../../../routes';
import { Link, useNavigate } from 'react-router-dom';
import { pushEvent } from '../../../utils/clevertapUtils';
import { UNREGISTERED_ORGANIZATION_SELECTED } from '../../../utils/clevertapEvents';

const saveOrganization = (
    dispatch: any,
    source: OrganizationSuggestionsResponse['source'],
) => {
    dispatch(
        signSetAction({
            ypId: source.ypId,
            doneeId: Number(source.doneeId) || undefined,
            doneeType: source.doneeType,
            organizationName: source.name,
            organizationAddress: source.address,
            organizationPhoneNumber: source.phone,
            organizationCity: source.city,
            organizationState: source.state,
            organizationZip: source.zip,
            lat: source.lat,
            lon: source.lon,
        }),
    );
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        modalStyles: {
            '& .heading': {
                fontSize: 24,
                marginBottom: theme.spacing(2),
            },
            '& .organization': {
                textAlign: 'left',
                padding: '15px 34px',
                border: '1px solid #E3E3E3',
                borderRadius: 8,
                margin: '22px 0',
            },
            '& .MuiTypography-body1': {
                fontSize: 14,
            },
            '& .submit': {
                marginBottom: 32,
            },
            '& .checkbox': {
                textAlign: 'left',
                marginBottom: 22,
            },
        },
    }),
);

interface ClaimOrganizationModalProps {
    isOpen: boolean;
    organization: OrganizationSuggestionsResponse;
    handleClose: () => unknown;
}

const ClaimOrganizationModal: React.FC<ClaimOrganizationModalProps> = ({
    isOpen,
    organization,
    handleClose,
}) => {
    const dispatch = useAppDispatch();
    const push = useNavigate();
    const { source } = organization;
    const { t } = useTranslation();
    const { modalStyles } = useStyles();
    const [checked, setChecked] = useState<boolean>(false);
    const { next } = useRouteConfig(PATH.SIGNUP.STEPS.SEARCH_ORGANIZATION);
    const onChange = (e: unknown, isChecked: boolean) => setChecked(isChecked);
    const onSave = () => saveOrganization(dispatch, source);
    const onClick = () => {
        saveOrganization(dispatch, source);
        pushEvent(UNREGISTERED_ORGANIZATION_SELECTED, { organization });
        next && push(next);
    };
    const {
        heading,
        subHeading,
        checkbox,
        confirmText,
        notAuthorized,
        someoneElseIsAuthorize,
    } = useMemo(
        () => ({
            heading: t('signup.organizationSearch.modal.heading'),
            subHeading: t('signup.organizationSearch.modal.subHeading'),
            checkbox: t('signup.organizationSearch.modal.checkbox'),
            confirmText: t('signup.organizationSearch.modal.confirmText'),
            notAuthorized: t('signup.organizationSearch.modal.notAuthorized'),
            someoneElseIsAuthorize: t(
                'signup.organizationSearch.modal.someoneElseIsAuthorize',
            ),
        }),
        [t],
    );
    return (
        <GivelifyModal
            label=""
            isOpen={isOpen}
            handleClose={handleClose}
            className={modalStyles}
        >
            <div className="heading">{heading}</div>
            <Label16>{subHeading}</Label16>
            <div className="organization">
                <Label18 isBold>{source.name}</Label18>
                <Label18>{source.address}</Label18>
                <Label18>
                    {source.city}, {source.state}
                </Label18>
                <Label18>{source.zip}</Label18>
            </div>
            <FormControlLabel
                control={
                    <Checkbox
                        size="small"
                        aria-label={checkbox}
                        checked={checked}
                        onChange={onChange}
                    />
                }
                label={checkbox}
                className="checkbox"
            />

            <Button
                variant="contained"
                disabled={!checked}
                className="submit"
                onClick={onClick}
            >
                {confirmText}
            </Button>
            <div>{notAuthorized}</div>
            <Link to={PATH.SIGNUP.STEPS.INVITE_DONEE} onClick={onSave}>
                {someoneElseIsAuthorize}
            </Link>
        </GivelifyModal>
    );
};

export default ClaimOrganizationModal;
